import { FC, SyntheticEvent, useMemo } from 'react'
import { FilterCriteriaValueFieldProps } from './FilterCriteriaValueFieldProps'
import { useQuery } from '@tanstack/react-query'
import { UserQueries } from '@portal/pkg-filter-criteria/user'
import { useSelectSingleMulti } from './useSelectSingleMulti'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Skeleton, TextField } from '@mui/material'
import { Loading } from '@portal/pkg-ui'
import { UserBasic } from '@portal/pkg-api/gen/shared'

export const FilterCriteriaValueUserList: FC<FilterCriteriaValueFieldProps> = ({
  operator,
  fieldProps: { value, onChange, error, ...fieldProps },
}) => {
  const { t } = useTranslation()

  const { data: users } = useQuery(UserQueries.users())

  const { calculatedValue, handleChange, multiple } = useSelectSingleMulti<number>({
    value,
    onChange,
    operator,
  })

  const handleChangeInternal = (
    event: SyntheticEvent,
    newValue: Array<UserBasic> | UserBasic | null,
  ) => {
    if (Array.isArray(newValue)) {
      const userIdArr = newValue.map(({ id }) => id)
      handleChange(event, userIdArr)
    } else {
      handleChange(event, newValue?.id || null)
    }
  }

  const calculatedValueInternal = useMemo(() => {
    if (Array.isArray(calculatedValue)) {
      return calculatedValue
        .map((id) => (users ?? []).find((u) => u.id === id))
        .filter(Boolean) as Array<UserBasic>
    } else {
      return (users ?? []).find((u) => u.id === calculatedValue) ?? null
    }
  }, [calculatedValue, users])

  return (
    <Loading until={{ users }} fallback={() => <Skeleton variant="rectangular" />}>
      {({ users }) => (
        <Autocomplete
          multiple={multiple}
          renderInput={(params) => <TextField variant="outlined" error={error} {...params} />}
          options={users}
          getOptionLabel={({ familyName, givenName }) =>
            t('displayName', { familyName, givenName })
          }
          isOptionEqualToValue={(user, value) => user.id === value.id}
          onChange={handleChangeInternal}
          value={calculatedValueInternal}
          slotProps={{ chip: { size: 'small' } }}
          {...fieldProps}
        />
      )}
    </Loading>
  )
}
